// HU-32b (MG-ILE-915) · tarea 32b-6. Administración › Usuarios: alta multi-rol, roles y ámbito,
// permiso L2 y estado. Contrato del AT S3 §2.2.2; nombres de `administracion/interfaces/http/esquemas.py`
// (`RolSalida`, `UsuarioSalida`, `ListaDeUsuariosSalida`, `AsignacionDeRolEntrada`).
//
// Todo lo derivado llega hecho del servidor (`estandares_frontend.md` §8.1): el nombre del rol, el
// texto del ámbito, del estado, del permiso L2 y del tipo, la invitación y las acciones de cada fila.
// La pantalla no traduce un código ni decide una acción.

import { pedir, segUuid } from './cliente';

/** Cómo se elige el ámbito de un rol (AT S3 §1.3, P-12). */
export type ReglaDeAmbito = 'corporativo' | 'un_pais' | 'uno_o_mas_paises' | 'paises_o_corporativo' | 'no_aplica';

/** Un rol del catálogo, en solo lectura (HU-32b-C6), con lo que el formulario necesita para no deducir. */
export type RolDelCatalogo = {
  codigo: string;
  nombre: string;
  descripcion: string | null;
  modulo_entrada: string | null;
  modulos: string[];
  activo: boolean;
  regla_de_ambito: ReglaDeAmbito;
  texto_de_la_regla: string;
  /** Se ofrece «Todos los países». */
  admite_todos_los_paises: boolean;
  minimo_de_paises: number;
  /** 0 = ámbito fijo: se muestra `texto_de_la_regla`, sin controles. `null` = sin tope. */
  maximo_de_paises: number | null;
  /** `false` = se muestra y no se asigna desde aquí (Especialista invitado, rol inactivo). */
  asignable: boolean;
  texto_de_no_asignable: string | null;
};

export type AsignacionDelUsuario = {
  rol: string;
  texto_del_rol: string;
  /** ISO-2. Vacío = corporativo. */
  paises: string[];
  texto_del_ambito: string;
};

export type AccionSobreUsuario = 'editar_roles' | 'conceder_l2' | 'revocar_l2' | 'desactivar' | 'reactivar';

export type UsuarioDelSistema = {
  usuario_id: string;
  nombre: string;
  correo: string;
  cargo: string | null;
  entra_oid: string | null;
  vinculado: boolean;
  asignaciones: AsignacionDelUsuario[];
  permiso_l2: boolean;
  texto_del_permiso_l2: string;
  activo: boolean;
  /** `activo` · `sin_acceso` (activo sin roles, HU-32b-C10) · `desactivado`. */
  estado: string;
  texto_del_estado: string;
  es_externo: boolean;
  texto_del_tipo: string | null;
  invitacion: { estado: string; texto: string; enviada_en: string | null } | null;
  acciones_permitidas: AccionSobreUsuario[];
  modulo_inicial: string | null;
};

export type ListaDeUsuarios = {
  usuarios: UsuarioDelSistema[];
  acciones_permitidas: 'alta'[];
};

/** Lo que viaja por rol: `paises` nulo o vacío = corporativo; el Representante exige uno (RN-60). */
export type AsignacionDeRolEntrada = { rol: string; paises: string[] | null };

export type AltaDeUsuario = {
  nombre: string;
  correo: string;
  cargo: string | null;
  asignaciones: AsignacionDeRolEntrada[];
};

const RUTA = '/administracion/usuarios';

export function listarRoles() {
  return pedir<RolDelCatalogo[]>('/administracion/roles');
}

export function listarUsuarios() {
  return pedir<ListaDeUsuarios>(RUTA);
}

/** HU-32b-C1, C2, C8. El alta no admite `permiso_l2`: se concede después, con motivo (C5). */
export function darDeAlta(alta: AltaDeUsuario) {
  return pedir<UsuarioDelSistema>(RUTA, { metodo: 'POST', cuerpo: alta });
}

/** HU-32b-C2, C7. Sustituye el conjunto ENTERO de roles y ámbitos. */
export function cambiarRoles(usuarioId: string, asignaciones: AsignacionDeRolEntrada[]) {
  return pedir<UsuarioDelSistema>(`${RUTA}/${segUuid(usuarioId)}/roles`, { metodo: 'PUT', cuerpo: { asignaciones } });
}

/** HU-32b-C5. Motivo obligatorio de 5 a 1000 caracteres (`PermisoL2Entrada`). */
export function cambiarPermisoL2(usuarioId: string, conceder: boolean, motivo: string) {
  return pedir<UsuarioDelSistema>(`${RUTA}/${segUuid(usuarioId)}/permiso-l2`, { metodo: 'PUT', cuerpo: { conceder, motivo } });
}

/** HU-32b-C4. Sin DELETE: los casos y los asientos lo siguen nombrando. */
export function cambiarEstado(usuarioId: string, activo: boolean) {
  return pedir<UsuarioDelSistema>(`${RUTA}/${segUuid(usuarioId)}`, { metodo: 'PATCH', cuerpo: { activo } });
}
